'use client';

import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Circle,
  FileText,
  Images,
  LayoutGrid,
  Lock,
  QrCode,
  Upload
} from 'lucide-react';

import { buildScanGridTemplate } from '@/lib/panel-resize';
import { API_ENDPOINTS } from '@/lib/api/config';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useUploadProgress } from '@/hooks/use-upload-progress';
import { useRouteAccess } from '@/hooks/use-route-access';
import { usePanelWidth } from '@/hooks/use-panel-width';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { UploadProgress } from '@/components/upload-progress';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import { photoScanningApi } from '../services/photo-scanning.api';
import { photoScanningQueries } from '../services/photo-scanning.queries';
import {
  useScanPageMutation,
  useScanFromImagesMutation,
  useScanFromCameraMutation,
  useCompleteBatchMutation,
  useRotatePageMutation,
  useTransferToQAMutation,
  useDeletePageMutation,
  useEnhancePageMutation
} from '../services/photo-scanning.mutations';
import { getPdfPageCount, renderPdfToImages } from '../utils/pdf-to-images';
import { buildInjectedPayload, injectQrIntoFile } from '../utils/inject-qr';
import { useDevQrInjectStore } from '../store/dev-qr-inject-store';
import { useQuarantineStore } from '../store/quarantine-store';
import {
  useLabelPrintConfig,
  useHardwareScannerEnabled,
  useScanPageQuota
} from '@/features/configurations';
import {
  classifyScannedQr,
  resolveExpectedContainers
} from '../utils/classify-qr';
import { decodeQrLocationFromBlob } from '@/lib/qr/decode';
import { DocumentPagesViewer, type ViewerPage } from './document-pages-viewer';
import {
  isProblemPage,
  type ScanBatch,
  type BatchPageRecord,
  type QrBboxData
} from '../types';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useRouter } from '@/i18n/routing';
import { useAuth } from '@/contexts/auth-context';
import { isDevToolsAllowedForEmail } from '@/features/dev-tools';

import { PHOTO_SCANNING_HASH } from '../constants';
import { MonitoringTab } from './monitoring-tab';
import { RearrangePagesDialog } from './rearrange-pages-dialog';
import { QrQuarantinePanel } from './qr-quarantine-panel';
import { ScanRightPanel } from './scan-right-panel';
import { PageThumbnailRail } from './page-thumbnail-rail';
import { QrPageGrid } from './qr-page-grid';
import { ScanProgressHeader } from './scan-progress-header';

function AttentionBanner({
  count,
  onReview
}: {
  count: number;
  onReview: () => void;
}) {
  const t = useTranslations('photoScanning');

  if (count === 0) return null;

  return (
    <div className='flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/30'>
      <AlertTriangle
        className='h-4 w-4 text-amber-600 dark:text-amber-400'
        aria-hidden
      />
      <span className='flex-1 text-sm font-medium text-amber-900 dark:text-amber-200'>
        {t('attentionBannerMessage', { count })}
      </span>
      <Button
        size='sm'
        variant='outline'
        className='h-7 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200'
        onClick={onReview}
      >
        {t('attentionBannerReview')}
      </Button>
    </div>
  );
}

/**
 * Thin vertical strip shown in a collapsed panel's grid column (desktop-only).
 * Keeps the re-open control right where the panel lives, so the toggle stays
 * next to its section in both states. Hidden on mobile, where panels stack.
 */
function PanelToggleStrip({
  icon,
  title,
  onExpand
}: {
  icon: React.ReactNode;
  title: string;
  onExpand: () => void;
}) {
  return (
    <div className='hidden shrink-0 flex-col items-center border-e bg-slate-50/60 py-2 lg:flex dark:bg-slate-900/40'>
      <button
        type='button'
        onClick={onExpand}
        title={title}
        aria-label={title}
        className='inline-flex h-7 w-7 items-center justify-center rounded-md border bg-white text-slate-500 shadow-sm transition-colors hover:border-violet-300 hover:text-violet-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-violet-300'
      >
        {icon}
      </button>
    </div>
  );
}

/**
 * Switches between the QR grid and the document viewer + thumbnail rail.
 * Document mode has no header of its own, so its caller wraps this in an
 * absolute-positioned pill; Grid mode instead renders it inline inside
 * QrPageGrid's own header row (as `viewToggle`) so it never collides with
 * that row's filter chips.
 */
function ViewModeToggle({
  viewMode,
  onChange
}: {
  viewMode: 'document' | 'grid';
  onChange: (mode: 'document' | 'grid') => void;
}) {
  const t = useTranslations('photoScanning');
  return (
    <div className='bg-background/90 flex items-center gap-0.5 rounded-lg border p-0.5 shadow-md backdrop-blur-sm'>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant={viewMode === 'document' ? 'secondary' : 'ghost'}
            size='icon'
            className='h-7 w-7'
            onClick={() => onChange('document')}
            aria-label={t('viewModeDocument')}
          >
            <FileText className='h-3.5 w-3.5' />
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom'>
          <p>{t('viewModeDocument')}</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size='icon'
            className='h-7 w-7'
            onClick={() => onChange('grid')}
            aria-label={t('viewModeGrid')}
          >
            <LayoutGrid className='h-3.5 w-3.5' />
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom'>
          <p>{t('viewModeGrid')}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/**
 * Pick the lowest-numbered empty slots so a bulk upload fills gaps (e.g.
 * slot 1) before tacking onto the tail. Without this, `actualPages.length +
 * 1` would skip over any unoccupied slot below the highest existing one —
 * a real bug after the operator scans a page out of order via a matched QR
 * (e.g. PG-005 first, then a PDF starts at slot 6 instead of slot 1).
 *
 * Walks beyond `total` if needed so the caller can still ship the array to
 * the backend, which will surface the over-capacity error with the correct
 * "remaining" math.
 */
function pickEmptyUploadSlots(
  occupiedSequences: number[],
  count: number,
  total: number
): number[] {
  const occupied = new Set(occupiedSequences);
  const result: number[] = [];
  const max = Math.max(total, occupiedSequences.length) + count + 1;
  let seq = 1;
  while (result.length < count && seq <= max) {
    if (!occupied.has(seq)) {
      result.push(seq);
    }
    seq++;
  }
  return result;
}

/**
 * Shape a decoded QR location into the persisted `{corners}` bbox the backend
 * stores on the row — same transform the bulk-upload handler applies. Returns
 * null when nothing was decoded.
 */
function cornersToQrBbox(
  loc: { corners: { x: number; y: number }[] } | null
): QrBboxData | null {
  return loc ? { corners: loc.corners } : null;
}

// ─── Execution tab ───────────────────────────────────────────────────────────

function ExecutionTab({ batchId }: { batchId: number }) {
  const t = useTranslations('photoScanning');
  const router = useRouter();
  const canAccess = useRouteAccess();
  const { user } = useAuth();
  const isDevTools = isDevToolsAllowedForEmail(user?.email);
  const scanMutation = useScanPageMutation();
  const completeMutation = useCompleteBatchMutation();
  const transferMutation = useTransferToQAMutation();
  const rotateMutation = useRotatePageMutation();
  const deletePageMutation = useDeletePageMutation();
  const enhancePageMutation = useEnhancePageMutation();
  const scanFromImagesMutation = useScanFromImagesMutation();
  const scanFromCameraMutation = useScanFromCameraMutation();

  // Byte-level upload progress shared by every upload trigger on this page
  // (upload images / PDF, replace image, per-card rescan, quarantine
  // re-upload / submit). Uploads fire sequentially, so one instance suffices.
  // This is distinct from the batch-completion `progressPct` in the header.
  const {
    progress: uploadProgress,
    onProgress: onUploadProgress,
    reset: resetUploadProgress
  } = useUploadProgress();

  const { data: rawBatch } = useQuery(photoScanningQueries.detail(batchId));
  const batch = rawBatch?.data as
    (ScanBatch & { pages: BatchPageRecord[] }) | undefined;

  // ── QR quarantine (local "needs action" hold) ──────────────────────────────
  // Non-success pages are held in the browser and never uploaded until the
  // operator resolves them. The store is scoped to the active batch and
  // cleared whenever the batch changes / the tab unmounts.
  // Dev QR injector sizes its synthetic QR to the configured physical label
  // footprint (square → smaller edge) so it matches a real printed sticker.
  const { labelWidthMm, labelHeightMm } = useLabelPrintConfig();
  const injectLabelMm = Math.min(labelWidthMm, labelHeightMm);

  const quarantineItems = useQuarantineStore((s) => s.items);
  const addQuarantineItems = useQuarantineStore((s) => s.addItems);
  const updateQuarantineItem = useQuarantineStore((s) => s.updateItem);
  const removeQuarantineItem = useQuarantineStore((s) => s.removeItem);
  const clearQuarantine = useQuarantineStore((s) => s.clear);
  // Overlay open-state lives in the URL hash (#quarantine, #rearrange) so the
  // panels are deep-linkable and close on browser Back. Destructure the stable
  // callbacks so they can go in hook dep arrays without tripping the React
  // Compiler's manual-memoization check.
  const { open: quarantineOpen, onOpenChange: toggleQuarantine } =
    useHashDialog(PHOTO_SCANNING_HASH.quarantine);
  const [scannerRescanTargetId, setScannerRescanTargetId] = useState<
    string | null
  >(null);
  // Persisted-page hardware re-scan target. When set, the DWT panel is armed
  // for a one-shot acquisition whose bytes replace this page's image (via the
  // `page:<id>` tag routed in handleScannerAcquire).
  const [scannerRescanPageId, setScannerRescanPageId] = useState<number | null>(
    null
  );
  // Document capture mode (configurations toggle): hardware scanner vs manual
  // upload. Gates the «الماسح» scanner panel, the manual upload buttons, and
  // the per-thumbnail hardware re-scan action. Defaults on.
  const scannerEnabled = useHardwareScannerEnabled();
  const [isSubmittingResolved, setIsSubmittingResolved] = useState(false);

  useEffect(() => {
    // Reset the hold queue when switching to a different batch and on unmount,
    // revoking every object URL so blobs don't leak across batches.
    clearQuarantine();
    return () => clearQuarantine();
  }, [batchId, clearQuarantine]);

  // The scanning keyboard-wedge stays enabled; the pause toggle UI is not
  // currently rendered, so this is always false (kept for the hook's `enabled`).
  const [isPaused] = useState(false);
  const [isAutoMode, setIsAutoMode] = useState(false);
  const { open: rearrangeOpen, onOpenChange: toggleRearrange } = useHashDialog(
    PHOTO_SCANNING_HASH.rearrange
  );
  // Collapsible side panels (desktop) — each collapses to a thin strip with a
  // re-open button in its own column, so the toggle stays next to its section.
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [showSidePanel, setShowSidePanel] = useState(true);
  // Both rails sit at the row's inline end, so both handles are on their
  // inline-start edge.
  const railResize = usePanelWidth('scan-thumb-rail', {
    defaultWidth: 216,
    min: 160,
    max: 360,
    edge: 'start'
  });
  const sidePanelResize = usePanelWidth('scan-side-panel', {
    defaultWidth: 272,
    min: 220,
    max: 520,
    edge: 'start'
  });
  // Grid mode replaces the document viewer + thumbnail rail entirely with a
  // QR-first grid (QrPageGrid) in the same column space. Defaults to grid;
  // Document mode is still one click away for search/print/download/fit.
  const [viewMode, setViewMode] = useState<'document' | 'grid'>('grid');
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  // Per-page cache-buster — bumps after a successful rotate so the browser
  // refetches the freshly rotated image instead of serving the stale blob.
  const [imageVersions, setImageVersions] = useState<Record<number, number>>(
    {}
  );
  const paperListEndRef = useRef<HTMLDivElement>(null);
  const uploadFileRef = useRef<HTMLInputElement>(null);
  const uploadPdfRef = useRef<HTMLInputElement>(null);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);

  // ── Derived state ─────────────────────────────────────────────────────────

  const pages: BatchPageRecord[] = useMemo(
    () => batch?.pages ?? [],
    [batch?.pages]
  );

  const total = batch?.total_pages ?? 0;
  const success = batch?.success_count ?? 0;
  const failed = batch?.failed_count ?? 0;
  // `remaining` is the number of *untouched* slots — i.e. slots that have no
  // row yet. It must mirror the backend's count (`onlyPages()->count()`) so
  // the upload guard and the displayed counter agree. Mismatched / unverified
  // pages are already occupying slots — they don't add to capacity, they
  // need to be rescanned in place.
  const scanned = batch?.scanned_pages ?? 0;
  // Held + accepted quarantine items are commitments the operator has already
  // made against the container's capacity even though they aren't on the
  // server yet — subtract them so a bulk upload can't over-commit slots that
  // pending holds will eventually claim.
  const heldCount = quarantineItems.length;
  const remaining = Math.max(0, total - scanned - heldCount);
  // System-wide scan-page ceiling (Settings → General). `Infinity` when no
  // limit is set, so it never constrains uploads. Enforced authoritatively by
  // the backend (422); this pre-blocks uploads so the operator doesn't waste a
  // round-trip once the whole system is full.
  const { data: scanQuotaData } = useScanPageQuota();
  const scanQuota = ((scanQuotaData as any)?.data ?? scanQuotaData) as
    { used: number; limit: number; unlimited: boolean } | undefined;
  const globalRemaining =
    scanQuota && !scanQuota.unlimited
      ? Math.max(0, scanQuota.limit - scanQuota.used)
      : Infinity;
  const progressPct = total > 0 ? Math.round((success / total) * 100) : 0;
  // The batch is only "done" when every slot is a `success` — a full grid
  // with any mismatched/unverified page still requires operator action, so
  // the scanner stays available.
  const isComplete = success >= total && total > 0;
  // Once the container has been transferred to QA (`qa_status` is set) it has
  // left the scanning operator's hands. The execution page becomes read-only:
  // no transfer, repair, page delete/re-image, rotate, rearrange or capture.
  const isHandedOff = batch?.qa_status != null;

  // Sort by `sequence` (not insertion order) so the viewer's page list, the
  // grid, and the "page N of M" counter all agree. Without this, an
  // out-of-order matched-QR upload (e.g. PG-005 saved first) would label
  // itself as "page 1" in the viewer while the grid correctly places it at
  // slot 5.
  const actualPages = useMemo(
    () =>
      pages
        .filter((p) => p.item_type === 'page')
        .slice()
        .sort((a, b) => a.sequence - b.sequence),
    [pages]
  );
  const lastScannedPage = useMemo(
    () => actualPages[actualPages.length - 1] ?? null,
    [actualPages]
  );

  // Container numbers this batch is allowed to scan — the backend's own
  // `expected_container_numbers`. Used by the client classifier to decide
  // whether a decoded page-QR is a clean match.
  const expectedContainers = useMemo<string[]>(
    () => resolveExpectedContainers(batch),
    [batch]
  );

  // Slot numbers already taken by a persisted `success` page — a manually
  // assigned quarantine slot can't collide with one of these.
  const occupiedPageNumbers = useMemo(
    () =>
      actualPages.filter((p) => p.status === 'success').map((p) => p.sequence),
    [actualPages]
  );

  const [gridFilter, setGridFilter] = useState<'all' | 'problems'>('all');

  const problemPages = useMemo(
    () => actualPages.filter(isProblemPage),
    [actualPages]
  );

  // Auto-reset the filter to 'all' when no problems remain (e.g. after the
  // operator removes the bad page) so they don't stare at an empty grid.
  useEffect(() => {
    if (problemPages.length === 0) setGridFilter('all');
  }, [problemPages.length]);

  useEffect(() => {
    setSelectedPageId(null);
  }, [actualPages.length]);

  const selectedPage =
    selectedPageId != null
      ? (actualPages.find((p) => p.id === selectedPageId) ?? lastScannedPage)
      : lastScannedPage;

  const selectedIndex =
    selectedPageId != null
      ? actualPages.findIndex((p) => p.id === selectedPageId)
      : actualPages.length - 1;

  // Map scanned pages → ViewerPage[] for DocumentPagesViewer.
  // Empty `chunks` arrays disable OCR overlays — this is a plain image viewer here.
  // Page bytes are served by the permissioned PAGE_IMAGE endpoint and pulled
  // through the authenticated blob cache by the viewer. The `?v=` cache-buster
  // forces a refetch after a server-side rotation (the LRU cache is keyed by
  // URL, so bumping the version evicts the old blob).
  const viewerPages = useMemo<ViewerPage[]>(
    () =>
      actualPages.map((p) => {
        const version = imageVersions[p.id] ?? 0;
        const endpoint = API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, p.id);
        const authEndpoint =
          version > 0 ? `${endpoint}?v=${version}` : endpoint;
        return {
          imageUrl: null,
          authEndpoint,
          // Label by the page's actual slot number, not its array index, so
          // an out-of-order matched-QR upload (PG-005 scanned first) reads
          // as "Page 5" instead of "Page 1" in the viewer.
          label: t('pageLabel', { n: p.sequence }),
          chunks: [],
          isProcessing: false,
          // Surface the QR verification status as a coloured polygon over
          // the printed QR in the preview — same palette the right-panel
          // problem chips use. The bbox is captured at upload time and
          // persisted on the row, so the overlay renders instantly without
          // re-decoding the served image.
          qrStatus: p.qr_status,
          qrLabel: p.barcode ?? p.page_number ?? null,
          qrBbox: p.qr_bbox ?? null,
          // Self-healing: when the viewer client-side-decodes a legacy row
          // (no persisted bbox), POST the corners back so future loads skip
          // the in-browser decode and render the overlay instantly.
          // Failures are silent — this is a best-effort backfill.
          onQrLocationDecoded: p.qr_bbox
            ? undefined
            : (corners) => {
                photoScanningApi
                  .updateQrBbox(batchId, p.id, { corners })
                  .catch(() => undefined);
              }
        };
      }),
    [actualPages, imageVersions, batchId, t]
  );

  // Stream the PDF through the authenticated API route so it works the same
  // in local-dev (local disk) and prod (GCS) — direct storage URLs aren't
  // reachable in dev because the file lives on the private disk.
  const pdfUrl = useAuthenticatedBlobUrl(
    batch?.pdf_path ? API_ENDPOINTS.PHOTO_SCANNING.BATCH_PDF(batchId) : null
  );

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (paperListEndRef.current) {
      paperListEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [pages.length]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleScanPage = useCallback(
    async (qrCode?: string) => {
      if (!qrCode) return;
      try {
        await scanMutation.mutateAsync({ batchId, qrCode });
      } catch {
        // handled globally
      }
    },
    [batchId, scanMutation]
  );

  useHardwareScanner({
    onScan: handleScanPage,
    enabled: !isPaused
  });

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (!isAutoMode && !scanMutation.isPending && !isComplete) {
          (document.activeElement as HTMLElement)?.blur();
        }
      } else if (e.code === 'KeyA') {
        e.preventDefault();
        setIsAutoMode((prev) => !prev);
      } else if (e.code === 'Escape') {
        router.back();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAutoMode, scanMutation.isPending, isComplete, router]);

  const handleTransferToQc = useCallback(async () => {
    try {
      if (batch?.status !== 'completed') {
        await completeMutation.mutateAsync(batchId);
      }
      await transferMutation.mutateAsync(batchId);
      // Handing the batch to QA does not make the scanner a reviewer: without
      // `qr_review.view` the QA queue answers "Access Denied", so a successful
      // transfer would end on an error screen. Go back to the scan list.
      router.push(
        canAccess('/dashboard/qr-review')
          ? '/dashboard/qr-review'
          : '/dashboard/photo-scanning'
      );
    } catch {
      // handled globally
    }
  }, [
    batch?.status,
    completeMutation,
    transferMutation,
    batchId,
    router,
    canAccess
  ]);

  const handleSelectPage = useCallback((pageId: number | null) => {
    setSelectedPageId(pageId);
  }, []);

  // Persist a page rotation on the server, keyed by page id. Shared by the
  // viewer's rotate toolbar (via `handleRotatePage` below, index-based) and
  // the QR grid / preview drawer (id-based, since the grid's list can be
  // filtered/reordered independently of the viewer's index space). After the
  // mutation resolves, photoScanningQueries.detail invalidates and the
  // affected page image refetches with the new rotation baked in.
  const handleRotatePageById = useCallback(
    async (pageId: number, delta: number) => {
      try {
        await rotateMutation.mutateAsync({ batchId, pageId, degrees: delta });
        // Bust the browser cache for this page so the freshly rotated bytes
        // are fetched. The viewer clears its optimistic CSS rotation when the
        // new blob arrives.
        setImageVersions((v) => ({
          ...v,
          [pageId]: (v[pageId] ?? 0) + 1
        }));
      } catch {
        // handled globally
      }
    },
    [batchId, rotateMutation]
  );

  const handleRotatePage = useCallback(
    (pageIndex: number, delta: number) => {
      const page = actualPages[pageIndex];
      if (!page) return;
      void handleRotatePageById(page.id, delta);
    },
    [actualPages, handleRotatePageById]
  );

  const handleDeletePage = useCallback(
    async (pageId: number) => {
      try {
        await deletePageMutation.mutateAsync({ batchId, pageId });
        setSelectedPageId(null);
      } catch {
        // handled globally
      }
    },
    [batchId, deletePageMutation]
  );

  // Per-card rescan: replaces the image bytes for one specific slot. Uses
  // `enhancePageMutation` (same endpoint the viewer's Replace button hits),
  // so the slot's barcode/sequence stay intact — only the bytes flip.
  const handleRescanPage = useCallback(
    async (pageId: number, file: File) => {
      const target = actualPages.find((p) => p.id === pageId);
      if (!target) return;
      try {
        const { mode: injectMode, sequenceOverride } =
          useDevQrInjectStore.getState();
        const payload = buildInjectedPayload(isDevTools, injectMode, {
          containerId: batch?.container_id ?? null,
          sequence: sequenceOverride ?? target.sequence
        });
        const transformed = await injectQrIntoFile(file, payload, {
          labelMm: injectLabelMm
        });
        await enhancePageMutation.mutateAsync(
          {
            batchId,
            pageId,
            image: transformed,
            onProgress: onUploadProgress
          },
          { onSettled: resetUploadProgress }
        );
      } catch {
        // handled globally
      } finally {
        useDevQrInjectStore.getState().setSequenceOverride(null);
      }
    },
    [
      batchId,
      batch?.container_id,
      actualPages,
      enhancePageMutation,
      isDevTools,
      injectLabelMm,
      onUploadProgress,
      resetUploadProgress
    ]
  );

  // ── Shared intake funnel ────────────────────────────────────────────────
  // Every input path (bulk images, PDF pages, scanner acquisitions, per-item
  // re-do) flows through here. Clean `success` pages are uploaded instantly;
  // everything else (mismatched / unverified) is HELD locally in the
  // quarantine and never sent until the operator resolves it.
  const intakeImages = useCallback(
    async (inputs: Blob[], opts?: { replaceItemId?: string }) => {
      if (inputs.length === 0) return;

      // Decode + classify each blob once. The same jsQR pass yields the bbox
      // so we never persist a code without its matching location.
      const decoded = await Promise.all(
        inputs.map((b) => decodeQrLocationFromBlob(b).catch(() => null))
      );

      // Per-item re-do: a single blob replaces a held item's bytes in place.
      if (opts?.replaceItemId) {
        const blob = inputs[0];
        const loc = decoded[0];
        const qrData = loc?.data ?? null;
        const cls = classifyScannedQr(qrData, expectedContainers);
        if (cls === 'success') {
          // Cleanly re-decoded — mark resolved so it submits WITH its QR. The
          // classification field is left untouched (it's only read for the
          // pending-state reason text, which is hidden once resolved).
          updateQuarantineItem(opts.replaceItemId, {
            blob,
            qrData,
            qrBbox: cornersToQrBbox(loc),
            resolution: 'resolved'
          });
        } else {
          // Still a problem after the re-do — keep it pending with the fresh
          // classification so the operator can try again or accept manually.
          updateQuarantineItem(opts.replaceItemId, {
            blob,
            qrData,
            qrBbox: cornersToQrBbox(loc),
            classification: cls,
            resolution: 'pending',
            assignedPageNumber: undefined
          });
          toast.info(t('heldForReview', { count: 1 }));
        }
        return;
      }

      type DecodedLoc = (typeof decoded)[number];
      const successBlobs: Blob[] = [];
      const successDecoded: DecodedLoc[] = [];
      const problems: Array<{
        blob: Blob;
        qrData: string | null;
        qrBbox: QrBboxData | null;
        classification: 'mismatched' | 'unverified';
      }> = [];

      inputs.forEach((blob, i) => {
        const loc = decoded[i];
        const qrData = loc?.data ?? null;
        const cls = classifyScannedQr(qrData, expectedContainers);
        if (cls === 'success') {
          successBlobs.push(blob);
          successDecoded.push(loc);
        } else {
          problems.push({
            blob,
            qrData,
            qrBbox: cornersToQrBbox(loc),
            classification: cls
          });
        }
      });

      // Upload the clean pages immediately. The real slot is decided by the
      // decoded QR server-side, so we just forward each page's code + bbox.
      if (successBlobs.length > 0) {
        const qrCodes = successDecoded.map((d) => d?.data ?? null);
        const qrBboxes: Array<QrBboxData | null> = successDecoded.map((d) =>
          cornersToQrBbox(d)
        );
        try {
          await scanFromImagesMutation.mutateAsync(
            {
              batchId,
              images: successBlobs,
              qrCodes,
              qrBboxes,
              onProgress: onUploadProgress
            },
            { onSettled: resetUploadProgress }
          );
        } catch {
          // handled globally
        }
      }

      // Hold every non-success page back for review.
      if (problems.length > 0) {
        addQuarantineItems(problems);
        toggleQuarantine(true);
        toast.info(t('heldForReview', { count: problems.length }));
      }
    },
    [
      batchId,
      expectedContainers,
      scanFromImagesMutation,
      addQuarantineItems,
      updateQuarantineItem,
      toggleQuarantine,
      t,
      onUploadProgress,
      resetUploadProgress
    ]
  );

  const handleAddFilesAsPages = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length === 0) return;

      if (files.length > globalRemaining) {
        toast.error(t('scanPageLimitReached'));
        return;
      }

      if (files.length > remaining) {
        toast.error(t('tooManyImages', { count: files.length, remaining }));
        return;
      }

      try {
        const { mode: injectMode, sequenceOverride } =
          useDevQrInjectStore.getState();
        const containerId = batch?.container_id ?? null;
        // Slot assignment is only meaningful for the dev QR injector, which
        // bakes the chosen `-P{n}` into the image. The real slot is decided by
        // the decoded QR (success) or by the operator (accepted), so we no
        // longer pre-assign sequences for the upload itself.
        const slotSequences =
          sequenceOverride != null
            ? files.map((_, i) => sequenceOverride + i)
            : pickEmptyUploadSlots(
                actualPages.map((p) => p.sequence),
                files.length,
                total
              );
        const images = await Promise.all(
          files.map((f, i) =>
            injectQrIntoFile(
              f,
              buildInjectedPayload(isDevTools, injectMode, {
                containerId,
                sequence: slotSequences[i]
              }),
              { labelMm: injectLabelMm }
            )
          )
        );
        await intakeImages(images);
      } catch {
        // handled globally
      } finally {
        useDevQrInjectStore.getState().setSequenceOverride(null);
      }
    },
    [
      batch?.container_id,
      actualPages,
      total,
      remaining,
      globalRemaining,
      intakeImages,
      t,
      isDevTools,
      injectLabelMm
    ]
  );

  const handleAddPdfAsPages = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      setIsProcessingPdf(true);
      try {
        const pageCount = await getPdfPageCount(file);

        if (pageCount === 0) {
          toast.error(t('pdfRenderFailed'));
          return;
        }
        if (remaining === 0) {
          toast.error(t('batchFull'));
          return;
        }
        if (globalRemaining <= 0) {
          toast.error(t('scanPageLimitReached'));
          return;
        }

        // Render at most `remaining` pages (and never past the system-wide
        // ceiling) — anything beyond capacity is dropped. The renderer's
        // maxPages cap also avoids wasting CPU on pages the backend would have
        // rejected.
        const pagesToUse = Math.min(pageCount, remaining, globalRemaining);
        const droppedPages = Math.max(0, pageCount - pagesToUse);

        if (droppedPages > 0) {
          toast.info(
            t('pdfPagesTruncated', {
              used: pagesToUse,
              total: pageCount,
              dropped: droppedPages
            })
          );
        }

        const rendered = await renderPdfToImages(file, {
          scale: 2,
          mimeType: 'image/jpeg',
          quality: 0.92,
          maxPages: pagesToUse
        });
        const { mode: injectMode, sequenceOverride } =
          useDevQrInjectStore.getState();
        const containerId = batch?.container_id ?? null;
        const slotSequences =
          sequenceOverride != null
            ? rendered.map((_, i) => sequenceOverride + i)
            : pickEmptyUploadSlots(
                actualPages.map((p) => p.sequence),
                rendered.length,
                total
              );
        const blobs = await Promise.all(
          rendered.map((r, i) => {
            const targetSeq = slotSequences[i];
            const payload = buildInjectedPayload(isDevTools, injectMode, {
              containerId,
              sequence: targetSeq
            });
            return payload
              ? injectQrIntoFile(
                  new File([r.blob], `page-${targetSeq}.jpg`, {
                    type: r.blob.type || 'image/jpeg'
                  }),
                  payload,
                  { labelMm: injectLabelMm }
                )
              : Promise.resolve(r.blob);
          })
        );
        await intakeImages(blobs);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('pdfRenderFailed'));
      } finally {
        setIsProcessingPdf(false);
        useDevQrInjectStore.getState().setSequenceOverride(null);
      }
    },
    [
      batch?.container_id,
      actualPages,
      total,
      remaining,
      globalRemaining,
      intakeImages,
      t,
      isDevTools,
      injectLabelMm
    ]
  );

  // ── Quarantine resolve handlers ─────────────────────────────────────────────

  // Scanner acquisition handoff — the embedded DWT panel calls this for every
  // acquired page. Three routings by the tag it carries:
  //  - `page:<id>` → a persisted-page hardware re-scan: replace that page's
  //    bytes via the enhance endpoint (same as the upload "Replace image").
  //  - any other `replaceItemId` → a quarantine held-item re-do.
  //  - no tag → a fresh acquisition through the intake funnel.
  const handleScannerAcquire = useCallback(
    async (blob: Blob, opts?: { replaceItemId?: string }) => {
      const tag = opts?.replaceItemId;
      if (tag && tag.startsWith('page:')) {
        const pageId = Number(tag.slice('page:'.length));
        if (Number.isFinite(pageId)) {
          try {
            await enhancePageMutation.mutateAsync(
              {
                batchId,
                pageId,
                image: blob,
                onProgress: onUploadProgress
              },
              { onSettled: resetUploadProgress }
            );
          } catch {
            // handled globally
          }
        }
        return;
      }
      return intakeImages([blob], opts);
    },
    [
      batchId,
      enhancePageMutation,
      intakeImages,
      onUploadProgress,
      resetUploadProgress
    ]
  );

  // Per-thumbnail hardware re-scan: arm the DWT panel to re-acquire one sheet
  // that replaces this persisted page's image, and reveal the scanner panel.
  const handleHardwareRescan = useCallback(
    (pageId: number) => {
      setScannerRescanPageId(pageId);
      setShowSidePanel(true);
      toast.info(t('rescanWithScanner'));
    },
    [t]
  );

  // Per-item re-upload (image or first PDF page already rendered by the panel).
  const handleReuploadItem = useCallback(
    (localId: string, blob: Blob) =>
      intakeImages([blob], { replaceItemId: localId }),
    [intakeImages]
  );

  // Arm the embedded scanner so its next one-shot acquisition replaces this
  // held item, then surface the scanner by closing the modal panel.
  const handleRescanItem = useCallback(
    (localId: string) => {
      setScannerRescanTargetId(localId);
      toggleQuarantine(false);
      toast.info(t('rescanWithScanner'));
    },
    [t, toggleQuarantine]
  );

  // Submit every resolved/accepted held item in one pass.
  //  - accepted → upload WITHOUT a QR, honestly numbered via expectedSequence.
  //  - resolved → upload WITH the freshly decoded clean QR + its bbox.
  const handleSubmitResolved = useCallback(async () => {
    const ready = quarantineItems.filter((i) => i.resolution !== 'pending');
    if (ready.length === 0) return;

    setIsSubmittingResolved(true);
    let added = 0;
    try {
      for (const item of ready) {
        try {
          if (item.resolution === 'accepted') {
            await scanFromCameraMutation.mutateAsync(
              {
                batchId,
                image: item.blob,
                expectedSequence: item.assignedPageNumber,
                qrCode: null,
                onProgress: onUploadProgress
              },
              { onSettled: resetUploadProgress }
            );
          } else {
            await scanFromCameraMutation.mutateAsync(
              {
                batchId,
                image: item.blob,
                qrCode: item.qrData,
                qrBbox: item.qrBbox,
                onProgress: onUploadProgress
              },
              { onSettled: resetUploadProgress }
            );
          }
          removeQuarantineItem(item.localId);
          added += 1;
        } catch {
          // handled globally — leave the item in the queue to retry
        }
      }
      if (added > 0) {
        toast.success(t('resolvedAdded', { count: added }));
      }
    } finally {
      setIsSubmittingResolved(false);
    }
  }, [
    quarantineItems,
    batchId,
    scanFromCameraMutation,
    removeQuarantineItem,
    t,
    onUploadProgress,
    resetUploadProgress
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className='flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-slate-900'>
      {/* ── HEADER (KPI strip) ── */}
      <ScanProgressHeader
        progressPct={progressPct}
        success={success}
        failed={failed}
        remaining={remaining}
        total={total}
        isComplete={isComplete}
        batch={batch}
        batchId={batchId}
        onTransferToQc={() => void handleTransferToQc()}
        isTransferPending={transferMutation.isPending}
        isHandedOff={isHandedOff}
      />

      {isHandedOff && (
        <div className='flex shrink-0 items-center gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200'>
          <Lock className='h-4 w-4 shrink-0' />
          <div className='text-xs'>
            <span className='font-semibold'>{t('qaHandoffBannerTitle')}</span>
            <span className='ms-1 opacity-80'>{t('qaHandoffBannerHint')}</span>
          </div>
        </div>
      )}

      {/* No expected page count → capacity is 0, so every capture path (scanner
          + manual upload) is blocked. Explain why instead of leaving the upload
          buttons silently disabled. */}
      {batch && total === 0 && !isHandedOff && (
        <div className='flex shrink-0 items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          <div className='text-xs'>
            <span className='font-semibold'>{t('noPageCountTitle')}</span>
            <span className='ms-1 opacity-80'>{t('noPageCountHint')}</span>
          </div>
        </div>
      )}

      {/* System-wide scan-page ceiling reached — every capture path is blocked
          server-side, so explain why the buttons don't work. */}
      {batch && total > 0 && globalRemaining === 0 && !isHandedOff && (
        <div className='flex shrink-0 items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          <div className='text-xs'>
            <span className='font-semibold'>{t('scanPageLimitTitle')}</span>
            <span className='ms-1 opacity-80'>{t('scanPageLimitHint')}</span>
          </div>
        </div>
      )}

      <AttentionBanner
        count={heldCount}
        onReview={() => toggleQuarantine(true)}
      />

      {/* ── BODY (grid on lg+, stacked on mobile) ──
          Document mode: [viewer | handle | thumbnail rail | handle | right panel].
          Grid mode: [QR grid | handle | right panel] — the thumbnail rail's
          columns disappear entirely rather than collapsing to a strip, since
          the grid already shows every page. */}
      <div
        className='flex min-h-0 flex-1 flex-col overflow-y-auto lg:grid lg:overflow-hidden'
        style={{
          // Inert below `lg`, where the container is still `flex`.
          // Widths are CSS variable expressions, so a drag repaints
          // without re-rendering this subtree.
          gridTemplateColumns: buildScanGridTemplate({
            railWidth: railResize.widthValue,
            panelWidth: sidePanelResize.widthValue,
            showThumbnails,
            showSidePanel,
            viewMode
          })
        }}
      >
        {/* ── CENTER — Document viewer or QR grid ── */}
        <div className='relative flex min-h-[60vh] min-w-0 flex-col bg-slate-100 lg:h-full lg:min-h-0 lg:border-e dark:bg-slate-950'>
          {viewMode === 'document' ? (
            <>
              <DocumentPagesViewer
                pages={viewerPages}
                pdfUrl={pdfUrl}
                highlightedChunkIds={[]}
                activeChunkIds={[]}
                activePage={selectedIndex >= 0 ? selectedIndex : 0}
                onPageChange={(idx) => {
                  const page = actualPages[idx];
                  if (page) setSelectedPageId(page.id);
                }}
                onRotatePage={isHandedOff ? undefined : handleRotatePage}
                isRotating={rotateMutation.isPending}
                onRearrangePages={
                  isHandedOff ? undefined : () => toggleRearrange(true)
                }
              />

              <RearrangePagesDialog
                open={rearrangeOpen}
                onOpenChange={toggleRearrange}
                batchId={batchId}
                pages={actualPages}
              />

              {/* Document mode has no header row of its own, so the
                  view-mode toggle floats as an absolute pill here. Grid
                  mode instead renders it inline via QrPageGrid's
                  `viewToggle` prop to avoid colliding with its filter
                  chips (see below). */}
              {/* <div className='absolute end-3 top-3 z-20'>
                <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
              </div> */}
            </>
          ) : (
            <QrPageGrid
              batchId={batchId}
              actualPages={actualPages}
              containerId={batch?.container_id ?? null}
              gridFilter={gridFilter}
              onSetGridFilter={setGridFilter}
              readOnly={isHandedOff}
              onSelectPage={handleSelectPage}
              onDeletePage={handleDeletePage}
              onRescanPage={handleRescanPage}
              scannerEnabled={scannerEnabled}
              onHardwareRescan={handleHardwareRescan}
              isDeletePending={deletePageMutation.isPending}
              isRescanPending={enhancePageMutation.isPending}
              uploadProgress={uploadProgress}
              onRotatePage={isHandedOff ? undefined : handleRotatePageById}
              isRotating={rotateMutation.isPending}
              imageVersions={imageVersions}
              // viewToggle={
              //   <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
              // }
            />
          )}

          {/* Manual capture actions — image upload / PDF upload.
              TEMP: previously dev-tools-only; opened up to all operators as the
              interim capture path until physical QR sheets + hardware scanning
              are fully rolled out. The «تجربة: حقن QR» injector panel stays
              hidden (dev-tools allowlist) and injection stays off for everyone
              but dev-tools users, so uploads carry no synthetic QR.
              Shown only in manual-upload capture mode — when the hardware
              scanner is the active mode, capture happens via the «الماسح»
              panel instead. */}
          {!isComplete && !isHandedOff && !scannerEnabled && (
            <div className='absolute start-3 top-3 z-20 flex flex-col gap-2'>
              <div className='flex flex-wrap items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-7 gap-1.5 text-xs shadow-md'
                  onClick={() => uploadFileRef.current?.click()}
                  disabled={scanFromImagesMutation.isPending || remaining === 0}
                >
                  <Upload className='h-3.5 w-3.5' />
                  {t('uploadImages')}
                </Button>
                <input
                  ref={uploadFileRef}
                  type='file'
                  accept='image/*'
                  multiple
                  className='hidden'
                  onChange={handleAddFilesAsPages}
                />
                <Button
                  variant='outline'
                  size='sm'
                  className='h-7 gap-1.5 text-xs shadow-md'
                  onClick={() => uploadPdfRef.current?.click()}
                  disabled={
                    isProcessingPdf ||
                    scanFromImagesMutation.isPending ||
                    remaining === 0
                  }
                >
                  <FileText className='h-3.5 w-3.5' />
                  {isProcessingPdf ? t('processingPdf') : t('uploadPdf')}
                </Button>
                <input
                  ref={uploadPdfRef}
                  type='file'
                  accept='application/pdf'
                  className='hidden'
                  onChange={handleAddPdfAsPages}
                />
              </div>
              {/* Byte-level upload bar — distinct from the header's batch
                  completion progress. Renders nothing while idle. */}
              <UploadProgress
                value={uploadProgress}
                className='w-56 rounded-md bg-white/90 p-1.5 shadow-md dark:bg-slate-900/90'
              />
            </div>
          )}
        </div>

        {/* Rail handle. Rendered as an empty grid item when the rail is
            collapsed: a `display:none` element is not a grid item, and
            every following child would shift a column. */}
        {viewMode === 'document' &&
          (showThumbnails ? (
            <PanelResizeHandle {...railResize.handleProps} />
          ) : (
            <div aria-hidden />
          ))}

        {/* ── PDF-STYLE THUMBNAIL RAIL (Document mode only, desktop, collapsible) ──
            Grid mode drops this column entirely — QrPageGrid already shows
            every page, so there's nothing left for the rail to add. */}
        {viewMode === 'document' &&
          (showThumbnails ? (
            <PageThumbnailRail
              batchId={batchId}
              actualPages={actualPages}
              total={total}
              activePageId={selectedPage?.id ?? null}
              onSelectPage={handleSelectPage}
              onClose={() => setShowThumbnails(false)}
              gridFilter={gridFilter}
              onSetGridFilter={setGridFilter}
              readOnly={isHandedOff}
              onDeletePage={handleDeletePage}
              onRescanPage={handleRescanPage}
              scannerEnabled={scannerEnabled}
              onHardwareRescan={handleHardwareRescan}
              isDeletePending={deletePageMutation.isPending}
              isRescanPending={enhancePageMutation.isPending}
              uploadProgress={uploadProgress}
            />
          ) : (
            <PanelToggleStrip
              icon={<Images className='h-4 w-4' />}
              title={t('toggleThumbnails')}
              onExpand={() => setShowThumbnails(true)}
            />
          ))}

        {/* ── RIGHT PANEL (collapsible) ── */}
        {showSidePanel ? (
          <PanelResizeHandle {...sidePanelResize.handleProps} />
        ) : (
          <div aria-hidden />
        )}
        {!showSidePanel && (
          <PanelToggleStrip
            icon={<QrCode className='h-4 w-4' />}
            title={t('sidePanelTitle')}
            onExpand={() => setShowSidePanel(true)}
          />
        )}
        <ScanRightPanel
          collapsed={!showSidePanel}
          onCollapse={() => setShowSidePanel(false)}
          batch={batch}
          batchId={batchId}
          nextSequence={actualPages.length + 1}
          progressPct={progressPct}
          failed={failed}
          success={success}
          remaining={remaining}
          total={total}
          isComplete={isComplete}
          isHandedOff={isHandedOff}
          scannerEnabled={scannerEnabled}
          onScannerAcquire={handleScannerAcquire}
          scannerRescanTargetId={
            scannerRescanTargetId ??
            (scannerRescanPageId != null ? `page:${scannerRescanPageId}` : null)
          }
          onScannerAcquireDone={() => {
            setScannerRescanTargetId(null);
            setScannerRescanPageId(null);
          }}
        />
      </div>

      {/* QR quarantine — held "needs action" pages awaiting resolution. */}
      <QrQuarantinePanel
        open={quarantineOpen}
        onOpenChange={toggleQuarantine}
        total={total}
        occupiedPageNumbers={occupiedPageNumbers}
        scannerAvailable={!isComplete && !isHandedOff}
        onRescanItem={handleRescanItem}
        onReuploadItem={handleReuploadItem}
        onSubmit={handleSubmitResolved}
        isSubmitting={isSubmittingResolved}
        uploadProgress={uploadProgress}
      />
    </div>
  );
}

// ─── Main execution component ────────────────────────────────────────────────

export function PhotoScanningExecution({ batchId }: { batchId: number }) {
  const t = useTranslations('photoScanning');
  const { data: rawBatch } = useQuery(photoScanningQueries.detail(batchId));
  const batch = rawBatch?.data as
    (ScanBatch & { pages: BatchPageRecord[] }) | undefined;

  return (
    <div className='flex h-full flex-col gap-4'>
      <div className='flex items-center justify-between px-1'>
        {/* <div className='flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400'>
          <Circle className='h-2 w-2 fill-emerald-500' />
          <span>{t('systemOnline')}</span>
        </div> */}
        {/* <div className='text-xs font-medium text-blue-600 dark:text-blue-400'>
          {t('batchNavigation', {
            lab: t('internalLab'),
            process: t('photoVerification'),
            batchId: batch?.batch_number ?? '...'
          })}
        </div> */}
      </div>

      <Tabs defaultValue='execution' className='flex min-h-0 flex-1 flex-col'>
        <TabsContent value='execution' className='min-h-0 flex-1'>
          <ExecutionTab batchId={batchId} />
        </TabsContent>
        <TabsContent value='monitoring'>
          <MonitoringTab batchId={batchId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
